// These functions generate transformations for the `page_content` field.
// They handle the common cases for CMS actions.

// Considerations:
// - For this initial implementation, indexing is managed using the array positioning,
//   which may become complicated with rendering and DnD in the future.

import { clone, concat, filter, get, isArray, map, size } from "lodash";

import {
  BuilderIOTabOptions,
  GalleryTabData,
  GalleryTabDataImage,
  GalleryTabDataVideo,
  GalleryTabOptions,
  MusicTabData,
  MusicTabDataOptions,
  ProductTabOptions,
  SKUPageContent,
  SKUPageContentGenericTabOptions,
  SKUPageContentOptions,
  SKUPageContentOptionsIntegrations,
  SKUPageContentOptionsSettings,
  SKUPageContentTab,
  SKUPageContentTabType,
  SKUPageContentTheme,
  SKUPageContentType,
  UniqueTabColumnType,
  UniqueTabData,
  UniqueTabOptions,
  WebpageTabOptions,
} from "@/data/types";

import { FileType, UploadedFile } from "./uploads";

export const MAX_TABS_PER_PAGE = 5;

export const MAX_TABS_PER_TYPE: Record<SKUPageContentTabType, number> = {
  [SKUPageContentTabType.PRODUCT_TAB]: 1,
  [SKUPageContentTabType.MUSIC_TAB]: MAX_TABS_PER_PAGE,
  [SKUPageContentTabType.GALLERY_TAB]: MAX_TABS_PER_PAGE,
  [SKUPageContentTabType.BUILDER_IO_TAB]: MAX_TABS_PER_PAGE,
  [SKUPageContentTabType.UNIQUE_TAB]: MAX_TABS_PER_PAGE,
  [SKUPageContentTabType.WEBPAGE_TAB]: MAX_TABS_PER_PAGE,
};

export const SUPPORTED_TAB_TYPES: SKUPageContentTabType[] = [
  SKUPageContentTabType.PRODUCT_TAB,
  SKUPageContentTabType.MUSIC_TAB,
  SKUPageContentTabType.GALLERY_TAB,
  SKUPageContentTabType.BUILDER_IO_TAB,
  SKUPageContentTabType.UNIQUE_TAB,
  SKUPageContentTabType.WEBPAGE_TAB,
];

export const CREATABLE_TAB_TYPES: SKUPageContentTabType[] = [
  SKUPageContentTabType.PRODUCT_TAB,
  SKUPageContentTabType.MUSIC_TAB,
  SKUPageContentTabType.GALLERY_TAB,
  SKUPageContentTabType.BUILDER_IO_TAB,
  SKUPageContentTabType.UNIQUE_TAB,
  SKUPageContentTabType.WEBPAGE_TAB,
];

const EMPTY_AUDIO_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.MUSIC_TAB,
  version: "1.0.0",
  data: [],
  options: {
    title: "",
    description: "",
    show_header_title: true,
    show_header_description: true,
    show_audio_artist_name: true,
    sync_with_tamper_status: false,
    is_downloadable_all: false,
  },
};

const EMPTY_BUILDER_IO_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.BUILDER_IO_TAB,
  version: "1.0.0",
  options: {
    builder_io_path: "",
    builder_io_api_key: "",
    sync_with_tamper_status: false,
  },
};

const EMPTY_GALLERY_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.GALLERY_TAB,
  version: "1.0.0",
  data: [],
  options: {
    title: "",
    description: "",
    show_header_title: true,
    show_header_description: true,
    show_content_titles: true,
    show_content_descriptions: true,
    sync_with_tamper_status: false,
  },
};

const EMPTY_PRODUCT_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.PRODUCT_TAB,
  version: "1.0.0",
  options: {},
};

const EMPTY_UNIQUE_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.UNIQUE_TAB,
  version: "1.0.0",
  data: [],
  options: {
    title: "",
    description: "",
    show_header_title: true,
    show_header_description: true,
    sync_with_tamper_status: false,
  },
};

const EMPTY_WEBPAGE_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.WEBPAGE_TAB,
  version: "1.0.0",
  options: {
    webpage_url: "",
    sync_with_tamper_status: false,
  },
};

const EmptyTabByType: Record<SKUPageContentTabType, SKUPageContentTab> = {
  [SKUPageContentTabType.MUSIC_TAB]: EMPTY_AUDIO_TAB,
  [SKUPageContentTabType.BUILDER_IO_TAB]: EMPTY_BUILDER_IO_TAB,
  [SKUPageContentTabType.PRODUCT_TAB]: EMPTY_PRODUCT_TAB,
  [SKUPageContentTabType.GALLERY_TAB]: EMPTY_GALLERY_TAB,
  [SKUPageContentTabType.UNIQUE_TAB]: EMPTY_UNIQUE_TAB,
  [SKUPageContentTabType.WEBPAGE_TAB]: EMPTY_WEBPAGE_TAB,
};

export const addTab = (
  pageContent: SKUPageContent,
  type: SKUPageContentTabType,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const newTab = get(EmptyTabByType, type);

  if (!newTab) {
    return pageContent;
  }

  const data = isArray(sanitized.data) ? sanitized.data : [];

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: concat(data, newTab),
  };
};

type UpdateAudioTabOptionsArgs = {
  title?: string;
  description?: string;
  show_header_title?: boolean;
  show_header_description?: boolean;
  show_audio_artist_name?: boolean;
  cover_image_url?: string;
};

export const updateAudioTabOptions = (
  pageContent: SKUPageContent,
  options: UpdateAudioTabOptionsArgs,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateProductTabOptions = (
  pageContent: SKUPageContent,
  options: ProductTabOptions,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isProductTab = tab.type === SKUPageContentTabType.PRODUCT_TAB;
    const isCurrent = index === tabIndex;

    if (!isProductTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateTabDetails = (
  pageContent: SKUPageContent,
  options: SKUPageContentGenericTabOptions,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isCurrent = index === tabIndex;

    if (!isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    } as SKUPageContentTab;
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const moveCMSTab = (
  pageContent: SKUPageContent,
  fromIndex: number,
  toIndex: number,
): SKUPageContent => {
  const newPageContentData = [...(pageContent.data ?? [])];

  return {
    ...pageContent,
    type: SKUPageContentType,
    data: reorder(newPageContentData, fromIndex, toIndex),
  };
};

export type PlaylistTrackItem = {
  url: string;
  file_name: string;
};

export const addPlaylistTracks = (
  pageContent: SKUPageContent,
  tabIndex: number,
  tracks: PlaylistTrackItem[],
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    const isDownloadableAll = get(tab.options, "is_downloadable_all", false);

    const createTrack = (track: PlaylistTrackItem): MusicTabData => ({
      type: "track",
      version: "1.0.0",
      options: {
        track_url: track.url,
        title: track.file_name,
        thumbnail_url: "",
        artist: "",
        isrc: "",
        is_downloadable: isDownloadableAll,
      },
    });

    return {
      ...tab,
      version: "1.0.0",
      data: [...tab.data, ...map(tracks, createTrack)],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updatePlaylistTrack = (
  pageContent: SKUPageContent,
  tabIndex: number,
  trackIndex: number,
  options: Partial<MusicTabDataOptions>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: map(tab.data, (track, i) => {
        if (i !== trackIndex) {
          return track;
        }

        return {
          ...track,
          options: {
            ...track.options,
            ...options,
          },
        };
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updatePlaylistTracks = (
  pageContent: SKUPageContent,
  tabIndex: number,
  updates: Array<{ trackIndex: number; options: Partial<MusicTabDataOptions> }>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    const updatesMap = new Map(map(updates, (u) => [u.trackIndex, u.options]));

    return {
      ...tab,
      data: map(tab.data, (track, trackIndex) => {
        const updateOptions = updatesMap.get(trackIndex);
        return updateOptions
          ? { ...track, options: { ...track.options, ...updateOptions } }
          : track;
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removePlaylistTrack = (
  pageContent: SKUPageContent,
  tabIndex: number,
  trackIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: filter(tab.data, (_, i) => i !== trackIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const movePlaylistTrack = (
  pageContent: SKUPageContent,
  tabIndex: number,
  trackIndex: number,
  newTrackIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: reorder(tab.data, trackIndex, newTrackIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateGalleryTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<GalleryTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export type GalleryItem = {
  url: string;
  file_name: string;
};

const createImage = (item: UploadedFile): GalleryTabDataImage => ({
  type: "image",
  version: "1.0.0",
  options: {
    title: item.fileName,
    description: "",
    image_url: item.url,
    is_downloadable: false,
  },
});

const createVideo = (item: UploadedFile): GalleryTabDataVideo => ({
  type: "video",
  version: "1.0.0",
  options: {
    title: item.fileName,
    description: "",
    video_url: item.url,
    is_downloadable: false,
  },
});

const createItem = (item: UploadedFile) => {
  if (item.fileType === FileType.IMAGE) {
    return createImage(item);
  } else if (item.fileType === FileType.VIDEO) {
    return createVideo(item);
  } else {
    throw new Error(`Unsupported file type: ${item.fileType}`);
  }
};

export const addGalleryItems = (
  pageContent: SKUPageContent,
  tabIndex: number,
  items: UploadedFile[],
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: [...tab.data, ...map(items, createItem)],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateGalleryItem = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
  options: Partial<GalleryTabData["options"]>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: map(tab.data, (item, i) => {
        if (i !== itemIndex) {
          return item;
        }

        return {
          ...item,
          options: {
            ...item.options,
            ...options,
          },
        } as GalleryTabData;
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removeGalleryItem = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: filter(tab.data, (_, i) => i !== itemIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const moveGalleryItem = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
  newItemIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: reorder(tab.data, itemIndex, newItemIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateBuilderIOTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<BuilderIOTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isBuilderTab = tab.type === SKUPageContentTabType.BUILDER_IO_TAB;
    const isCurrent = index === tabIndex;

    if (!isBuilderTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateSettings = (
  pageContent: SKUPageContent,
  settings: NonNullable<SKUPageContentOptionsSettings>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  return {
    ...sanitized,
    options: {
      ...sanitized.options,
      settings: {
        ...sanitized.options?.settings,
        ...settings,
      },
    } as SKUPageContentOptions,
  };
};

export const updateIntegrations = (
  pageContent: SKUPageContent,
  integrations: NonNullable<SKUPageContentOptionsIntegrations>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  return {
    ...sanitized,
    options: {
      ...sanitized.options,
      integrations: {
        ...sanitized.options?.integrations,
        ...integrations,
      },
    } as SKUPageContentOptions,
  };
};

export const updateUniqueTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<UniqueTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isUniqueTab = tab.type === SKUPageContentTabType.UNIQUE_TAB;
    const isCurrent = index === tabIndex;

    if (!isUniqueTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const addUniqueTabColumnMapping = (
  pageContent: SKUPageContent,
  tabIndex: number,
  columnName: string,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isUniqueTab = tab.type === SKUPageContentTabType.UNIQUE_TAB;
    const isCurrent = index === tabIndex;

    if (!isUniqueTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      data: [
        ...tab.data,
        {
          type: "column_mapping",
          version: "1.0.0",
          options: {
            column_name: columnName,
            column_type: UniqueTabColumnType.TEXT,
            is_downloadable: false,
          },
        },
      ],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateUniqueTabColumnMapping = (
  pageContent: SKUPageContent,
  tabIndex: number,
  columnMappingIndex: number,
  values: {
    columnName: string;
    columnType: UniqueTabColumnType;
    isDownloadable: boolean;
  },
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isUniqueTab = tab.type === SKUPageContentTabType.UNIQUE_TAB;
    const isCurrent = index === tabIndex;

    if (!isUniqueTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      data: map(tab.data, (columnMapping, i) => {
        if (i !== columnMappingIndex) {
          return columnMapping;
        }

        return {
          ...columnMapping,
          version: "1.0.0",
          options: {
            ...columnMapping.options,
            column_name: values.columnName,
            column_type: values.columnType,
            is_downloadable: values.isDownloadable,
          },
        } as UniqueTabData;
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removeUniqueTabColumnMapping = (
  pageContent: SKUPageContent,
  tabIndex: number,
  columnMappingIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isUniqueTab = tab.type === SKUPageContentTabType.UNIQUE_TAB;
    const isCurrent = index === tabIndex;

    if (!isUniqueTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      data: filter(tab.data, (_, i) => i !== columnMappingIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removeAllColumnMappings = (
  pageContent: SKUPageContent,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab): SKUPageContentTab => {
    const isUniqueTab = tab.type === SKUPageContentTabType.UNIQUE_TAB;

    if (!isUniqueTab) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      data: [],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateWebpageTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<WebpageTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isWebpageTab = tab.type === SKUPageContentTabType.WEBPAGE_TAB;
    const isCurrent = index === tabIndex;

    if (!isWebpageTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

// When validation on the backend silently fails, we may end up with empty
// tab objects. This ensures that they are cleaned up properly on subsequent
// saves.
// We may consider doing this automatically after saves, but ultimately, the
// server should reject empty tabs.
// We also prepend the product tab if it doesn't exist.

export const sanitizePageContent = (
  content: SKUPageContent,
): SKUPageContent => ({
  ...content,
  type: SKUPageContentType,
  data: sanitizePageContentData(content),
  options: {
    settings: sanitizePageContentOptionsSettings(content),
    integrations: sanitizePageContentOptionsIntegrations(content),
  },
});

const sanitizePageContentData = (
  content: SKUPageContent,
): SKUPageContentTab[] => {
  const validTabs = filter(content.data, (tab) => {
    return SUPPORTED_TAB_TYPES.includes(tab.type);
  });

  const hasTabs = size(validTabs) >= 1;
  return hasTabs ? validTabs : [EMPTY_PRODUCT_TAB];
};

const sanitizePageContentOptionsSettings = (
  content: SKUPageContent,
): SKUPageContentOptionsSettings => {
  const settings = get(content, "options.settings");

  return {
    theme: get(settings, "theme", SKUPageContentTheme.DARK),
  };
};

const sanitizePageContentOptionsIntegrations = (
  content: SKUPageContent,
): SKUPageContentOptionsIntegrations => {
  const integrations = get(content, "options.integrations");

  const google = get(integrations, "google_analytics.measurement_id", "");
  const linkedin = get(integrations, "linkedin_insight.partner_id", "");
  const meta = get(integrations, "meta_pixel.tracking_id", "");

  return {
    google_analytics: {
      measurement_id: google,
    },
    linkedin_insight: {
      partner_id: linkedin,
    },
    meta_pixel: {
      tracking_id: meta,
    },
  };
};

export const deleteTab = (
  pageContent: SKUPageContent,
  index: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: filter(sanitized.data, (_, i) => i !== index),
  };
};

export const getGalleryItemKey = (item: GalleryTabData) => {
  switch (item.type) {
    case "image":
      return item.options.image_url;
    case "video":
      return item.options.video_url;
    default:
      console.log("Unsupported media type");
      return undefined;
  }
};

const reorder = <T>(arr: T[], origin: number, target: number) => {
  const isValidOrigin = origin >= 0 && origin <= arr.length - 1;
  const isValidTarget = target >= 0 && target <= arr.length - 1;
  const isSameIndex = origin === target;

  const result = clone(arr);

  if (isSameIndex || !isValidOrigin || !isValidTarget) {
    return result;
  }

  const [item] = result.splice(origin, 1);
  result.splice(target, 0, item);

  return result;
};
